import {
  Inbox, Send, FileText, Star, Trash2, Archive, ShieldAlert, PenSquare, Settings, Clock, Plus,
} from 'lucide-react';
import { FOLDERS } from '../../lib/folders';
import { colorOf, labelFolderId } from '../../lib/labels';
import { getNavItems } from './IconRail';

const ICONS = { Inbox, Send, FileText, Star, Trash2, Archive, ShieldAlert, Clock };

const formatKb = (kb) => {
  if (kb >= 1024 * 1024) return `${(kb / (1024 * 1024)).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} GB`;
  if (kb >= 1024) return `${Math.round(kb / 1024).toLocaleString('tr-TR')} MB`;
  return `${kb} KB`;
};

const itemClass = (active) =>
  `flex items-center gap-3 w-full px-3 h-10 rounded-lg text-sm transition-colors ${
    active
      ? 'bg-accent text-accent-foreground font-medium'
      : 'text-foreground/80 hover:bg-accent/60'
  }`;

// Mail sayfasının klasör paneli. Mobilde çekmece içinde `showNav` ile bölüm menüsü de gösterilir.
export const Sidebar = ({
  currentFolder,
  onFolderChange,
  onCompose,
  counts = {},
  quota = null,
  labels = [],
  onManageLabels,
  user,
  showNav = false,
  currentPage,
  onNavigate,
  className = '',
}) => {
  const rawPct = quota && quota.limit_kb > 0 ? Math.min(100, (quota.used_kb / quota.limit_kb) * 100) : 0;
  const usedPct = Math.round(rawPct);
  const pctLabel = rawPct > 0 && rawPct < 1 ? '<%1' : `%${usedPct}`;
  const displayName = (user?.display_name || '').trim();

  return (
    <aside
      className={`w-[260px] shrink-0 bg-panel border-r border-border flex flex-col ${className}`}
      data-testid="folder-panel"
    >
      <div className="p-4">
        <button
          onClick={onCompose}
          className="w-full h-11 rounded-lg bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center gap-2 shadow-sm hover:bg-primary/90 transition-colors"
          data-testid="compose-button"
        >
          <PenSquare className="h-4 w-4" />
          Yeni e-posta
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin px-3 pb-3">
        {showNav && (
          <div className="mb-3 pb-3 border-b border-border space-y-0.5">
            {getNavItems(user).map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  className={itemClass(currentPage === item.id)}
                  data-testid={`drawer-${item.testId}`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span>{item.label}</span>
                </button>
              );
            })}
            <button
              onClick={() => onNavigate('settings')}
              className={itemClass(currentPage === 'settings')}
              data-testid="drawer-nav-settings"
            >
              <Settings className="h-4 w-4 shrink-0" />
              <span>Ayarlar</span>
            </button>
          </div>
        )}

        <div className="space-y-0.5">
          {FOLDERS.map((folder) => {
            const Icon = ICONS[folder.icon] || Inbox;
            const active = currentFolder === folder.id && (!showNav || currentPage === 'mail');
            const count = counts[folder.id];
            return (
              <button
                key={folder.id}
                onClick={() => onFolderChange(folder.id)}
                className={itemClass(active)}
                data-testid={`folder-${folder.id}`}
              >
                <Icon className="h-[18px] w-[18px] shrink-0" />
                <span className="flex-1 text-left truncate">{folder.name}</span>
                {count > 0 && (
                  folder.id === 'inbox' ? (
                    <span className="min-w-[24px] h-5 px-1.5 rounded-full bg-primary/15 text-primary text-xs font-semibold flex items-center justify-center">
                      {count}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">{count}</span>
                  )
                )}
              </button>
            );
          })}
        </div>

        {/* Etiketler */}
        <div className="mt-4" data-testid="label-section">
          <div className="flex items-center justify-between px-3 mb-1">
            <h3 className="text-xs font-semibold text-muted-foreground">Etiketler</h3>
            <button onClick={onManageLabels} className="p-1 rounded hover:bg-accent/60 text-muted-foreground" aria-label="Etiketleri yönet" data-testid="manage-labels">
              <Plus className="h-4 w-4" />
            </button>
          </div>
          {labels.length === 0 ? (
            <button onClick={onManageLabels} className="px-3 text-xs text-muted-foreground hover:text-foreground text-left">Etiket oluştur…</button>
          ) : (
            <div className="space-y-0.5">
              {labels.map((label) => {
                const id = labelFolderId(label.id);
                return (
                  <button
                    key={label.id}
                    onClick={() => onFolderChange(id)}
                    className={itemClass(currentFolder === id && (!showNav || currentPage === 'mail'))}
                    data-testid={`label-folder-${label.id}`}
                  >
                    <span className={`h-2.5 w-2.5 mx-[3px] rounded-full shrink-0 ${colorOf(label.color).dot}`} />
                    <span className="flex-1 text-left truncate">{label.name}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Depolama (sunucu kota bildiriyorsa) */}
      {quota && (
        <div className="px-5 py-3 border-t border-border" data-testid="storage-meter">
          <p className="text-xs text-foreground/80 mb-2">
            {formatKb(quota.used_kb)} / {formatKb(quota.limit_kb)} ({pctLabel})
          </p>
          <div className="h-1.5 rounded-full bg-border overflow-hidden" role="progressbar" aria-valuenow={usedPct} aria-valuemin={0} aria-valuemax={100}>
            <div
              className={`h-full rounded-full ${usedPct >= 90 ? 'bg-destructive' : 'bg-primary'}`}
              style={{ width: `${usedPct}%` }}
            />
          </div>
        </div>
      )}

      <div className="px-5 py-3 border-t border-border">
        {displayName && <p className="text-sm font-medium truncate" title={displayName}>{displayName}</p>}
        <p className="text-xs text-muted-foreground truncate" title={user?.email}>{user?.email}</p>
      </div>
    </aside>
  );
};

export default Sidebar;
